import { useSuspenseQuery } from '@tanstack/react-query';

import { getRequest } from '@/api/rest/hooks/requests/get';
import {
  Conversation,
  GET_ORGANIZATION_CONVERSATIONS_PATH,
  GetOrganizationConversationsErrorDto,
  GetOrganizationConversationsSuccessDto,
} from '@/api/rest/resources/types/conversation';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { assertIsDefined } from '@/utils/assert';

type ConversationsSuccessDto = GetOrganizationConversationsSuccessDto;
type ConversationsErrorDto = GetOrganizationConversationsErrorDto;

export const useOrganizationConversations = (organizationIdArg?: string, filters?: Record<string, string>) => {
  const paramOrgId = useOrganizationId();
  const organizationId = organizationIdArg || paramOrgId;

  assertIsDefined(organizationId, 'organizationId');

  const pathVariables = { organization_id: organizationId };
  const queryParams = filters ?? undefined;

  const queryFn = () => {
    return getRequest<ConversationsSuccessDto>(GET_ORGANIZATION_CONVERSATIONS_PATH, pathVariables, queryParams);
  };

  return useSuspenseQuery<ConversationsSuccessDto, ConversationsErrorDto>({
    queryKey: ['organization', pathVariables, 'conversations', queryParams],
    queryFn,
    staleTime: 1000 * 60 * 5,
  });
};

export const useOrganizationConversation = (conversationIdArg: string, organizationIdArg?: string) => {
  const paramOrgId = useOrganizationId();
  const organizationId = organizationIdArg || paramOrgId;

  assertIsDefined(organizationId, 'organizationId');

  const conversations = useOrganizationConversations(organizationId, { conversation_id: conversationIdArg }).data;

  return conversations.find((conversation: Conversation) => conversation.id === conversationIdArg);
};

export const useActiveConversationTitle = (conversationIdArg: string, organizationIdArg?: string) => {
  const conversation = useOrganizationConversation(conversationIdArg, organizationIdArg);
  return conversation?.title ?? '';
};
